const WS_URL = (() => {
  const params = new URLSearchParams(window.location.search);
  const wsParam = params.get('ws');
  if (wsParam) return wsParam;

  const envWsUrl = import.meta.env.VITE_WS_URL;
  if (envWsUrl) return envWsUrl;

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
})();

export class SignalingClient {
  constructor() {
    this.ws = null;
    this.sessionId = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 3;
    this.intentionalClose = false;
  }

  isOpen() {
    return Boolean(this.ws && this.ws.readyState === WebSocket.OPEN);
  }

  connect(sessionId) {
    this.sessionId = sessionId;

    if (this.isOpen()) {
      return Promise.resolve();
    }

    this.closeSocket({ emit: false });

    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(WS_URL);

        this.ws.onopen = () => {
          console.log('WebSocket connected');
          this.reconnectAttempts = 0;
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('Failed to parse message:', error);
          }
        };

        this.ws.onclose = () => {
          console.log('WebSocket closed');
          const emit = !this.intentionalClose;
          this.intentionalClose = false;
          this.ws = null;
          if (emit) this.emit('close');
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          reject(error);
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  handleMessage(message) {
    const { type } = message;
    this.emit(type, message);
  }

  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.error('WebSocket not connected');
    }
  }

  joinQueue() {
    this.send({
      type: 'JOIN_QUEUE',
      sessionId: this.sessionId,
    });
  }

  leave() {
    this.send({ type: 'LEAVE' });
    this.close();
  }

  // Leave current session but stay connected (for finding new match while waiting)
  leaveSession() {
    this.send({ type: 'FIND_NEW_MATCH' });
    // Don't close the WebSocket - keep it open
  }

  rejoinQueue() {
    // Rejoin queue with same sessionId (for reconnection)
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.send({
        type: 'JOIN_QUEUE',
        sessionId: this.sessionId,
      });
    }
  }

  sendOffer(offer) {
    this.send({
      type: 'OFFER',
      data: offer,
    });
  }

  sendAnswer(answer) {
    this.send({
      type: 'ANSWER',
      data: answer,
    });
  }

  sendIceCandidate(candidate) {
    this.send({
      type: 'ICE_CANDIDATE',
      data: candidate,
    });
  }

  reconnect() {
    this.send({
      type: 'RECONNECT',
      sessionId: this.sessionId,
    });
  }

  cancelReconnect() {
    this.send({
      type: 'CANCEL_RECONNECT',
      sessionId: this.sessionId,
    });
  }

  close() {
    this.closeSocket({ emit: false });
  }

  closeSocket({ emit } = { emit: false }) {
    if (!this.ws) return;

    this.intentionalClose = !emit;
    const socket = this.ws;
    this.ws = null;

    if (!emit) {
      socket.onclose = null;
      socket.onerror = null;
    }

    if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
      socket.close();
    }
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index !== -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in event listener:', error);
        }
      });
    }
  }
}
